import { MessageProxy } from "@intelligems/ig-embedded-preview";

const proxies = new Map<string, MessageProxy>();

export const messageProxyFactory = {
  /**
   * Register a MessageProxy for an iframe. If one is already registered
   * under `id`, destroy it and create a fresh instance.
   *
   * Reusing the existing proxy would be unsafe: its listeners may be
   * bound to a removed iframe node (HMR, an aborted close that left the
   * proxies map populated, or a rapid close+reopen race). A fresh
   * instance guarantees consistent state. The warning preserves
   * observability so we can still catch lifecycle bugs — resilience
   * here should not hide a missed `destroy` path in prod.
   */
  create(id: string): MessageProxy {
    if (proxies.has(id)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[messageProxyFactory] Duplicate create for id="${id}" — previous close path did not run destroy(). Recreating.`
      );
      proxies.get(id)?.destroy();
      proxies.delete(id);
    }
    const proxy = new MessageProxy({
      getTargetWindow: () => {
        const el = document.getElementById(id);
        return el instanceof HTMLIFrameElement
          ? (el.contentWindow ?? undefined)
          : undefined;
      },
    });
    proxy.attach();
    proxies.set(id, proxy);
    return proxy;
  },

  get(id: string): MessageProxy | undefined {
    return proxies.get(id);
  },

  destroy(id: string): void {
    proxies.get(id)?.destroy();
    proxies.delete(id);
  },
};
